/**
 * OPTIBOARD TABS — ONE CLASSIFIER: the canonical production state.
 *
 * History. 8G.2C.7 found the six PostgREST tab filters, `getProductionState`
 * and the state→tab mapper giving three answers (a Picked Up Incomplete car sat
 * in Scheduled). 8G.2C.8 collocated an in-memory predicate with the SQL filters,
 * but they were still two implementations of the lifecycle, and they kept
 * drifting:
 *
 *   - Scheduled required `scheduled_date >= today`, while the canonical model
 *     calls any unfinished ticket with a dispatch plan `scheduled` whatever its
 *     date. An unfinished ticket scheduled YESTERDAY matched no tab at all and
 *     vanished from the board at midnight.
 *   - WIP required `timer_status IN (running, paused)`; a held ticket whose
 *     timer is stopped (status Paused / paused_at) matched Scheduled or nothing.
 *   - No filter knew `archived_at`, `status = 'Deleted'`, parking or plan-less
 *     rows, so those landed in zero or two tabs.
 *
 * The law now (fix/foh-optiboard-calendar-truth):
 *
 *   TAB = getOptiBoardLifecycleTabForProductionState(getProductionState(row))
 *
 * exactly one tab, or `null` for the deliberately hidden states
 * (`parking_lot_inactive`, `archived`). There is no second implementation.
 *
 * The SERVER only partitions what to download, on the two terminal facts
 * `getProductionState` ranks first (`deleted_at`, then `picked_up_at`):
 *
 *     Live      deleted_at IS NULL AND picked_up_at IS NULL
 *     PickedUp  deleted_at IS NULL AND picked_up_at IS NOT NULL
 *     Deleted   deleted_at IS NOT NULL
 *
 * Disjoint and exhaustive, so every ticket is downloaded exactly once and then
 * classified only by the canonical helper. (The board downloaded all six tabs,
 * unpaginated, before; this is three queries for the same rows.)
 */
import {
  getProductionState,
  type ProductionStateTicketShape,
} from '@/utils/ticketProductionState';
import {
  getOptiBoardLifecycleTabForProductionState,
  type OptiBoardLifecycleTab,
} from '@/utils/optiBoardLifecycleTab';

/** The ticket fields the classification reads — the canonical shape. */
export type OptiBoardTabRow = ProductionStateTicketShape;

export type OptiBoardTab = OptiBoardLifecycleTab;

export const OPTIBOARD_TABS: readonly OptiBoardTab[] = Object.freeze([
  'Estimates', 'Scheduled', 'WIP', 'Completed', 'PickedUp', 'Deleted',
]);

/** The one tab this ticket belongs to, or null when it is deliberately hidden. */
export function optiBoardTabFor(row: OptiBoardTabRow): OptiBoardTab | null {
  return getOptiBoardLifecycleTabForProductionState(getProductionState(row));
}

/**
 * Does `row` belong in `tab`? `_today` is accepted for compatibility and
 * deliberately unused: a calendar date never moves a ticket between lifecycle
 * stages (an overdue unfinished ticket is still Scheduled work).
 */
export function isInOptiBoardTab(row: OptiBoardTabRow, tab: OptiBoardTab, _today?: string): boolean {
  return optiBoardTabFor(row) === tab;
}

/** Every tab a row lands in: exactly one, or none for a hidden state. */
export function optiBoardTabsFor(row: OptiBoardTabRow, _today?: string): OptiBoardTab[] {
  const tab = optiBoardTabFor(row);
  return tab ? [tab] : [];
}

// ── What to download ────────────────────────────────────────────────────────
export type OptiBoardFetchScope = 'Live' | 'PickedUp' | 'Deleted';

export const OPTIBOARD_FETCH_SCOPES: readonly OptiBoardFetchScope[] = Object.freeze(['Live', 'PickedUp', 'Deleted']);

/** Which download a row comes from — the in-memory mirror of the filters below. */
export function optiBoardFetchScopeFor(row: { deleted_at?: string | null; picked_up_at?: string | null }): OptiBoardFetchScope {
  if (row.deleted_at != null) return 'Deleted';
  if (row.picked_up_at != null) return 'PickedUp';
  return 'Live';
}

export type PostgrestLike = {
  is: (c: string, v: unknown) => PostgrestLike;
  not: (c: string, op: string, v: unknown) => PostgrestLike;
};

/**
 * The download partition as PostgREST filters. The generic is unconstrained
 * and the cast is confined here (8G.2C.8) so call sites keep their real types.
 */
export function applyOptiBoardFetchScope<T>(q: T, scope: OptiBoardFetchScope): T {
  const b = q as unknown as PostgrestLike;
  const out = (x: PostgrestLike): T => x as unknown as T;
  switch (scope) {
    case 'Live':
      return out(b.is('deleted_at', null).is('picked_up_at', null));
    case 'PickedUp':
      return out(b.not('picked_up_at', 'is', null).is('deleted_at', null));
    case 'Deleted':
      return out(b.not('deleted_at', 'is', null));
    default:
      return q;
  }
}

/**
 * Core-100 R2 — CORE-AS-GAP-003: Picked Up and Deleted grow forever, and a PostgREST response is
 * capped (1,000 rows by default) with no signal — so those tabs silently showed an arbitrary
 * subset once a shop was old enough. They are now the most recent ARCHIVE_WINDOW rows, newest
 * first, fetched as WINDOW + 1 so the surface can SAY when older ones exist.
 */
export const OPTIBOARD_ARCHIVE_WINDOW = 200;

/** PostgREST's default row cap: a Live result this size may be incomplete. */
export const POSTGREST_DEFAULT_ROW_CAP = 1000;

type OrderLimitLike = { order: (c: string, o: { ascending: boolean }) => OrderLimitLike; limit: (n: number) => OrderLimitLike };

export function boundOptiBoardArchive<T>(q: T, scope: OptiBoardFetchScope): T {
  if (scope === 'Live') return q;
  const b = q as unknown as OrderLimitLike;
  return b.order(scope === 'PickedUp' ? 'picked_up_at' : 'deleted_at', { ascending: false })
    .limit(OPTIBOARD_ARCHIVE_WINDOW + 1) as unknown as T;
}

export function trimOptiBoardArchive<R>(rows: readonly R[]): { rows: R[]; truncated: boolean } {
  return { rows: rows.slice(0, OPTIBOARD_ARCHIVE_WINDOW), truncated: rows.length > OPTIBOARD_ARCHIVE_WINDOW };
}

// ── Grouping ────────────────────────────────────────────────────────────────
export type OptiBoardTabGroups<R> = Record<OptiBoardTab, R[]> & { hidden: R[] };

const desc = (field: string) => (a: Record<string, unknown>, b: Record<string, unknown>) =>
  String(b[field] ?? '').localeCompare(String(a[field] ?? ''));
const ascNullsLast = (...fields: string[]) => (a: Record<string, unknown>, b: Record<string, unknown>) => {
  for (const f of fields) {
    const x = a[f]; const y = b[f];
    if (x == null && y == null) continue;
    if (x == null) return 1;
    if (y == null) return -1;
    const c = String(x).localeCompare(String(y));
    if (c !== 0) return c;
  }
  return 0;
};

/** Each tab's order — the orders the six per-tab queries used. */
const TAB_ORDER: Record<OptiBoardTab, (a: Record<string, unknown>, b: Record<string, unknown>) => number> = {
  Estimates: desc('created_at'),
  Scheduled: ascNullsLast('scheduled_date', 'start_time'),
  WIP: desc('created_at'),
  Completed: desc('completed_at'),
  PickedUp: desc('picked_up_at'),
  Deleted: desc('deleted_at'),
};

/**
 * Classify downloaded rows into tabs. A row seen twice (a refetch racing a
 * write) is kept once, by id.
 */
export function groupOptiBoardRows<R extends OptiBoardTabRow & { id?: unknown }>(rows: readonly R[]): OptiBoardTabGroups<R> {
  const groups = {
    Estimates: [], Scheduled: [], WIP: [], Completed: [], PickedUp: [], Deleted: [], hidden: [],
  } as OptiBoardTabGroups<R>;
  const seen = new Set<unknown>();
  for (const row of rows) {
    if (row.id != null) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
    }
    const tab = optiBoardTabFor(row);
    (tab ? groups[tab] : groups.hidden).push(row);
  }
  for (const tab of OPTIBOARD_TABS) {
    groups[tab].sort(TAB_ORDER[tab] as (a: R, b: R) => number);
  }
  return groups;
}
